import type { DatovkaApiType } from '@gov-nx/api';
import type {
	FileUploadHook,
	FileUploadItem,
	PersonCommunicationData,
	ProcessControlCore,
} from '@gov-nx/core/hooks';
import type { useCountry } from '@gov-nx/core/service';
import type { DataBoxFormData, RadioBooleanOption } from '@gov-nx/ui/types';
import type { ServiceWizardContextCore } from '../types/serviceContext.types';

export type ServiceContextTypes = {
	isDataBoxConnected: boolean;
	requiredDataBoxes: DatovkaApiType['typSchranky'][];
	personCommunicationData: PersonCommunicationData;
	groupTargetKeys: Array<keyof typeof TrestniOznameniGroupTarget>;
	allCountriesForSelect: ReturnType<typeof useCountry>['allCountriesForSelect'];
	printscreenFileUploadHook: FileUploadHook;
	attachmentsFileUploadHook: FileUploadHook;
	isLowAuthorization: boolean;
	isAuthorizationEnforced: boolean;
} & ServiceWizardContextCore<ProcessControlCore, TrestniOznameniFormData>;

export enum TrestniOznameniGroupTarget {
	skupinyKtereSeOdlisujiUrcitymiTypickymiZnaky = 'Skupiny, které se odlišují určitými typickými znaky (např. barvou pleti)',
	etnickeANarodnostiSkupiny = 'Etnické a národnostní skupiny',
	skupinyDefinovaneVyznanim = 'Skupiny definované vyznáním',
	sexualniMensiny = 'Sexuální menšiny',
	skupinyDefinovanePolitickymPresvedcenim = 'Skupiny definované politickým přesvědčením',
	jine = 'Jiné',
}

export enum TrestniOznameniGender {
	man = 'Muž',
	woman = 'Žena',
	unknown = 'Unknown',
}

export interface DataRequest {
	params: {
		odesilatelDSId: DataBoxFormData['datovaSchrankaId'];
		naseCisloJednaci?: DataBoxFormData['naseCisloJednaci'];
		naseSpisovaZnacka?: DataBoxFormData['naseSpisovaZnacka'];
	};
	body: {
		kodPodani: 'HATECRIME2';

		popisProblemu: string;
		vedenoProtiSkupine: TrestniOznameniGroupTarget[];
		vedenoProtiSkupinePopis: string[];

		datumZjisteni?: string;
		verejnePristupny: boolean;
		verejnePristupnyKomu?: string;
		printscreen?: FileUploadItem['fileId'][];
		priloha?: FileUploadItem['fileId'][];

		pachatelJmenoPrijmeni?: string;
		pachatelUlice?: string;
		pachatelObec?: string;
		pachatelPsc?: string;
		pachatelStatniPrislusnost?: string;
		pachatelPohlavi?: TrestniOznameniGender;
		pachatelVek?: number;
		pachatelLink?: string;
		pachatelJine?: string;
		pachatelZjisten?: string;

		obetJmenoPrijmeni?: string;
		obetUlice?: string;
		obetObec?: string;
		obetPsc?: string;
		obetPohlavi?: TrestniOznameniGender;
		obetVek?: number;
		obetLink?: string;
		obetJine?: string;

		zadostOVyrozumeni: boolean;
		kontaktTel?: string;
		kontaktEmail?: string;
	};
}

export interface TrestniOznameniFormDataStep1 {
	popisProblemu: DataRequest['body']['popisProblemu'];

	skupinyKtereSeOdlisujiUrcitymiTypickymiZnaky?: boolean;
	skupinyKtereSeOdlisujiUrcitymiTypickymiZnakyPopis?: string;

	etnickeANarodnostiSkupiny?: boolean;
	etnickeANarodnostiSkupinyPopis?: string;

	skupinyDefinovaneVyznanim?: boolean;
	skupinyDefinovaneVyznanimPopis?: string;

	sexualniMensiny?: boolean;
	sexualniMensinyPopis?: string;

	skupinyDefinovanePolitickymPresvedcenim?: boolean;
	skupinyDefinovanePolitickymPresvedcenimPopis?: string;

	jine?: boolean;
	jinePopis?: string;
}

export interface TrestniOznameniFormDataStep2 {
	datumZjisteni?: Date | null;
	verejnePristupny?: keyof typeof RadioBooleanOption;
	verejnePristupnyKomu: DataRequest['body']['verejnePristupnyKomu'];
	printscreen?: FileUploadItem[];
	priloha?: FileUploadItem[];
}

export interface TrestniOznameniFormDataStep3 {
	jePachatelZnam: RadioBooleanOption;
	pachatelJmenoPrijmeni: DataRequest['body']['pachatelJmenoPrijmeni'];
	pachatelUlice: DataRequest['body']['pachatelUlice'];
	pachatelObec: DataRequest['body']['pachatelObec'];
	pachatelPsc: DataRequest['body']['pachatelPsc'];
	pachatelStatniPrislusnost: DataRequest['body']['pachatelStatniPrislusnost'];
	pachatelPohlavi: DataRequest['body']['pachatelPohlavi'];
	pachatelVek: DataRequest['body']['pachatelVek'];
	pachatelZjisten: DataRequest['body']['pachatelZjisten'];
	pachatelLink: DataRequest['body']['pachatelLink'];
	pachatelJine: DataRequest['body']['pachatelJine'];
}

export interface TrestniOznameniFormDataStep4 {
	jeObetZnama: RadioBooleanOption;
	obetJmenoPrijmeni: DataRequest['body']['obetJmenoPrijmeni'];
	obetUlice: DataRequest['body']['obetUlice'];
	obetObec: DataRequest['body']['obetObec'];
	obetPsc: DataRequest['body']['obetPsc'];
	obetPohlavi: DataRequest['body']['obetPohlavi'];
	obetVek: DataRequest['body']['obetVek'];
	obetJine: DataRequest['body']['obetJine'];
	obetLink: DataRequest['body']['obetLink'];
}

export interface TrestniOznameniFormDataStep5 {
	zadostOVyrozumeni: keyof typeof RadioBooleanOption;
	_useKontaktTel?: boolean;
	kontaktTel?: string;
	_useKontaktEmail?: boolean;
	kontaktEmail?: string;
}

export type TrestniOznameniFormDataStep6 = DataBoxFormData;

export type TrestniOznameniFormData = TrestniOznameniFormDataStep1 &
	TrestniOznameniFormDataStep2 &
	TrestniOznameniFormDataStep3 &
	TrestniOznameniFormDataStep4 &
	TrestniOznameniFormDataStep5 &
	TrestniOznameniFormDataStep6;
